// @givmo/employer-module — register the company for an account that already exists (NF-FIX21).
//
// One registration step, shared by its two callers so they cannot drift:
//   - the details screen's PROVIDER path (`?provider=true`): a Google or Microsoft account, or an email
//     account sent back there after a refused registration;
//   - the code screen's `?register=true` arrival: the email sign-up, once its code is confirmed.
// Both reach it with an account that has its Givmo row and a proven work email, so a refusal never
// deletes the account (`completeProviderRegistration`): the caller shows the message and the person
// tries again.
//
// The step is the details screen's pre-NF-FIX21 sequence, moved here unchanged: with join-or-create on,
// resolve the onboarding branch FIRST (a joinable domain pivots to request-access instead of creating a
// duplicate; an existing admin goes into the portal), then register; the create's own request-access
// result is the belt-and-suspenders pivot. Flag-off, the resolve is skipped.
//
// Both hosts run it (EP-MNT8) through their own auth context and address book (`useSignUpHost()`).

import { useNavigate } from '@tanstack/react-router';
import { COMPANY_ONBOARDING_OUTCOME } from '@givmo/auth';
import { isCompanyJoinOrCreateEnabled } from '../../lib/env';
import { useSignUpHost } from './sign-up-host';
import { useSignUpJoin } from './sign-up-join-context';
import type { SignUpDetails } from './sign-up-context';

/**
 * `routed` — the step navigated on its own (the request-access pivot, or an existing admin into the
 * portal); `created` — the company now exists and the caller picks the next screen. A refused
 * registration THROWS the registration's `AuthError`.
 */
export type RegisterCompanyOutcome = 'routed' | 'created';

export function useRegisterCompany(): (details: SignUpDetails) => Promise<RegisterCompanyOutcome> {
  const { auth, paths } = useSignUpHost();
  const { resolveDomain, completeProviderRegistration } = auth;
  const navigate = useNavigate();
  const { setTarget } = useSignUpJoin();

  // EP-SUITE2 pre-emptive resolve. Returns true when it has ROUTED the user (a pivot handled here),
  // false to fall through to the normal create. A resolve that fails (403 not-yet-verified /
  // transient) or says CREATE_AS_OWNER / BLOCKED falls through; the create's own requestAccess result
  // is the belt-and-suspenders.
  const resolveAndRoute = async (): Promise<boolean> => {
    const resolution = await resolveDomain().catch(() => null);
    if (resolution === null) return false;
    switch (resolution.outcome) {
      case COMPANY_ONBOARDING_OUTCOME.requestAccess:
        if (resolution.company === null) return false; // malformed target → fall through to create
        setTarget(resolution.company);
        await navigate({ to: paths.signUpRequestAccess });
        return true;
      case COMPANY_ONBOARDING_OUTCOME.alreadyMember:
      case COMPANY_ONBOARDING_OUTCOME.enableProduct:
        // Already an admin — don't dead-end. (v1 routes ENABLE_PRODUCT into the portal too; the
        // org-console product-enable surface is EP-SUITE3.)
        await navigate({ to: paths.dashboard });
        return true;
      default:
        // CREATE_AS_OWNER / BLOCKED / any unknown outcome → proceed with the normal create.
        return false;
    }
  };

  return async (details) => {
    if (isCompanyJoinOrCreateEnabled() && (await resolveAndRoute())) {
      return 'routed';
    }
    const registration = await completeProviderRegistration({
      companyName: details.companyName,
      numberOfEmployees: details.numberOfEmployees,
      firstName: details.firstName,
      lastName: details.lastName,
    });
    if (registration.outcome === 'requestAccess') {
      // Belt-and-suspenders: a request-access domain the pre-emptive resolve missed.
      setTarget(registration.company);
      await navigate({ to: paths.signUpRequestAccess });
      return 'routed';
    }
    return 'created';
  };
}
